import { Pill, type PillTone } from './Pill';

/**
 * The semantic tone of a structure-state note. Kept distinct from the visual
 * {@link PillTone} so callers reason in domain terms (excluded / empty /
 * needs-authoring) while this component owns the colour mapping.
 */
export type StructureStateTone = 'excluded' | 'empty' | 'needs-authoring' | 'review' | 'override';

/**
 * A presentational descriptor for ONE honest structure-state surface (F-017
 * US2): the short label is the at-a-glance signal, `detail` is the
 * machine-generated, PHI-free reason surfaced as the accessible tooltip.
 */
export type StructureNote = {
  readonly label: string;
  readonly detail: string;
  readonly tone: StructureStateTone;
};

const toPillTone: Record<StructureStateTone, PillTone> = {
  excluded: 'danger',
  empty: 'neutral',
  'needs-authoring': 'warning',
  review: 'warning',
  override: 'info',
};

/**
 * The honest structure-state chip. It NEVER hides a node — it explains WHY a
 * node is excluded, empty, or awaiting authoring. The machine reason is both
 * the hover tooltip (`title`) and the accessible name (`aria-label`), so the
 * "why" is reachable by sighted and assistive-tech users alike (FR-012).
 */
export function StructureStateChip({ note }: { note: StructureNote }) {
  return (
    <Pill
      tone={toPillTone[note.tone]}
      size="sm"
      dot
      title={note.detail}
      aria-label={`${note.label}: ${note.detail}`}
    >
      {note.label}
    </Pill>
  );
}
